import { keepPreviousData, useInfiniteQuery } from '@tanstack/react-query';

import { useAccounts, useAppSelector } from '@pkg/hooks';
import { getNextPageParam } from '@pkg/hooks/deprecated';

import { YatPages } from '@app/client';
import { type PageItemsSearchParams } from '@app/client/resources/yat-pages/page-items';
import { getQueryItems, type QueryItemsType, serializeResponseItems } from '@app/store/actions/nft-manager';

export const useNFTManagerSearchQuery = () => {
    const searchValue = useAppSelector((state) => state.nftManager.managerSearchValue);
    const currentFilters = useAppSelector((state) => state.nftManager.managerFilters);

    const pageId = useAppSelector((state) => state.yatPage.id);

    const operator = searchValue ? 'OR' : 'AND';
    const queryItems: QueryItemsType = getQueryItems({ searchValue, operator, currentFilters });

    const searchQuery: PageItemsSearchParams = {
        query: queryItems,
        operator, // This is made because the search value checks for item name or collection name
        limit: '16',
        dir: currentFilters.sortingOrder || 'Desc',
        sort: currentFilters.sortingField || 'activity_date',
        yat_page_id: pageId,
    };

    return { searchQuery };
};

export const useGetNfts = () => {
    const { connectedWallets } = useAccounts();
    const syncedWallets = connectedWallets?.map((item) => item.address);

    const { searchQuery } = useNFTManagerSearchQuery();

    const queryParams = searchQuery?.query
        ? searchQuery.query.map((param) => Object.entries(param).map(([key, value]) => `${key}-${value}`)).flat()
        : ['none'];

    const cacheKey = syncedWallets
        ? [...queryParams, searchQuery?.dir, searchQuery?.sort, syncedWallets.join('')]
        : queryParams;

    // Prevent empty items
    const cleanCacheKeys = cacheKey.reduce<string[]>((acc, value) => {
        if (value) {
            acc.push(value);
        }
        return acc;
    }, []);

    return useInfiniteQuery({
        queryKey: cleanCacheKeys,
        queryFn: ({ pageParam = 0, signal }) =>
            YatPages.YatPageItems.callGetNFTs({
                ...searchQuery,
                page: pageParam?.toString() || '0',
                settings: { signal },
            }).then((res) => ({
                ...res,
                data: serializeResponseItems(res.data),
            })),

        retry: false,
        placeholderData: keepPreviousData,
        initialPageParam: 0,
        getNextPageParam,
    });
};
